export { Role, type User } from '@/types/user';

type ChannelId = '10DLC' | 'RCS' | 'SMS';

export interface AttachmentInfo {
  uuid: string;
  fileName: string;
  mimeType: string;
}

export interface CreateAppealParams {
  evpUid: string;
  appealCategories: string[];
  attachmentUuids: string[];
  explanation: string;
  name?: string;
}

export enum BrandAssetType {
  Banner = 'BANNER',
  Logo = 'LOGO',
}

export enum BrandAssetStatus {
  Pending = 'PENDING',
  Verified = 'VERIFIED',
  Unverified = 'UNVERIFIED',
  Expired = 'EXPIRED',
}

export interface BrandAssetParams {
  brandAssetType?: BrandAssetType;
  name?: string;
  verificationStatus?: BrandAssetStatus;
}
export interface BrandAsset {
  name: string;
  type: BrandAssetType;
  attachmentUuid: string;
  verificationStatus?: BrandAssetStatus;
  reasons?: string;
  createDate?: number;
  verifiedDate?: number;
  validUntil?: number;
  expirationDate?: number;
}

export interface BrandAssetResult {
  brandUid: string;
  brandIdentityHash: string;
  name: string;
  assetType: string;
  attachmentUuid: string;
  createDate: number;
  updateDate: number;
}

export interface BrandSuggestionParams {
  prefix?: string; // the displayName
  companyNamePrefix?: string; // the companyName
  limit?: number;
  status?: string;
}

export interface BrandSuggestion {
  brandUid: string;
  brandName: string; // the displayName
  brandCompanyName: string;
  market: string;
}

export interface ResellerSuggestionParams {
  companyNamePrefix: string;
  resellerUid?: string;
  limit?: number;
}

export interface ResellerSuggestion {
  resellerUid: string;
  companyName: string;
  cspUid: string;
}

export interface CspCampaignSharing {
  campaignUid: string;
  cspUid: string;
  downstreamCnpUid: string;
  status?: string;
  explanation?: string;
  createDate: number;
  updateDate: number;
  upstreamCnpUid?: string;
  upstreamCnpName?: string;
}

export interface CnpItem {
  uid: string;
  displayName: string;
}

export interface HostingRegion {
  id: string;
  name: string;
}

export interface BillingCategory {
  id: string;
  name: string;
  description: string;
}

export interface RcsCnp {
  id: string;
  name: string;
}

export interface RcsAsset {
  uuid?: string;
  url?: string;
  name: string;
  type: 'logo' | 'banner';
  status?: 'pending' | 'verified';
}

export interface RcsMno {
  id: string;
  name: string;
  qualify: boolean;
  mnoPreview: boolean;
  billingCategory: string;
  fallbackSupported: boolean | null;
}
export interface RejectCampaignParams {
  explanation: string;
  rejectionCategory: string[];
}

export interface UseCaseParams {
  channelId?: ChannelId;
}

export interface UseCase {
  id: string;
  classification: string;
  displayName: string;
  description: string;
  monthlyFee: number;
  quarterlyFee: number;
  annualFee: number;
  validSubUsecase: boolean;
  minSubUsecases: number;
  maxSubUsecases: number;
  maxTn?: number;
}

export interface MnoMetadata {
  id: string;
  minMsgSamples: number;
  mno: string;
  mnoReview: boolean;
  mnoSupport: boolean;
  noEmbeddedLink: boolean;
  noEmbeddedPhone: boolean;
  qualify: boolean;
  reqSubscriberHelp: boolean;
  reqSubscriberOptin: boolean;
  reqSubscriberOptout: boolean;
  billingCategory?: string;
  fallbackSupported?: boolean;
  // Only available for AT&T
  tpm?: number;
  tpmScope?: string;
  mmsTpm?: number;
  msgClass?: string;
  // Only available for T-Mobile
  brandDailyCap?: number;
  brandTier?: string;
}

export interface CampaignBuilderMetadataParams {
  channelId?: ChannelId;
}

export interface CampaignBuilderMetadata {
  usecase: string;
  greyoutFields: string[];
  mnoMetadata: Record<string, MnoMetadata>;
}

export interface CampaignBuilderResult {
  campaignUid: string;
  mnoMetadata: Record<string, MnoMetadata>;
}

export interface ResellerParams {
  recordsPerPage?: number;
}

export interface Reseller {
  uid: string;
  cspUid: string;
  companyName: string;
  email: string;
  phone: string;
  createDate: number;
  updateDate: number;
}

export interface Brand {
  uid: string;
  referenceId: string | null;
  cspUid: string;
  status: string;
  displayName: string;
  entityType: string;
  companyName: string | null;
  firstName: string | null;
  lastName: string | null;
  ein: string | null;
  einIssuingCountry: string;
  altBusinessId: string | null;
  altBusinessIdType: string | null;
  phone: string;
  street: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  email: string;
  stockSymbol: string | null;
  stockExchange: string | null;
  brandRelationshipScore: number;
  website: string | null;
  submissionCount: number;
  vettedDate: number | null;
  vertical: string;
  evpUid: string | null;
  evpVettingId: string | null;
  evpVettingScore: number | null;
  evpVettingClass: string | null;
  trustLevel: number;
  evpVettingDate: number | null;
  createDate: number;
  updateDate: number;
  mobilePhone: string | null;
  mobilePhoneVerifiedDate: number | null;
  universalEin: string | null;
  identityStatus: string;
  optionalAttributes?: Record<string, string>;
  businessContactEmail?: string;
  businessContactEmailVerifiedDate?: number;
  businessContactFirstName?: string;
  businessContactLastName?: string;
  businessContactTitle?: string;
  loaStatus: string | null;
  trademarkStatus: string | null;
}

export interface VettingRecordParams {
  evpUid?: string;
  vettingClass?: string;
  vettingStatus?: string;
}

export interface VettingRecord {
  evpUid: string;
  brandUid: string;
  vettingClass: string;
  vettingId: string;
  vettingToken: string | null;
  tcrBillable: boolean;
  tcrBillAmount: number | null;
  vettedDate: number | null;
  score: number | null;
  fullReport: string | null;
  vettingStatus: string;
  reasons: string | null;
  additionalRequestPayload: string | null;
  createDate: number;
  updateDate: number;
  expirationDate: number | null;
  validUntil: number | null;
}

export interface VettingClass {
  id: string;
  displayName: string;
  description: string;
  validityMonths: number;
  enabled: boolean;
}

export type VettingClassType =
  | 'STANDARD'
  | 'ENHANCED'
  | 'POLITICAL'
  | 'EXEMPT'
  | 'POLITICAL_FEDERAL'
  | 'POLITICAL_STATE'
  | 'POLITICAL_LOCAL'
  | 'RCS'
  | 'AUTHPLUS';

export interface ImportVettingInstruction {
  info: string;
  parameters: {
    vettingId: { label: string; show: boolean };
    vettingToken: { label: string; show: boolean };
  };
  supported: boolean;
}

export interface NewVettingInstruction {
  info: string;
  price: {
    [key in VettingClassType]?: number;
  };
  supported: boolean;
  waitTime: string;
}

export interface VettingProvider {
  uid: string;
  displayName: string;
  vettingInstruction: {
    importVet: ImportVettingInstruction;
    newVet: NewVettingInstruction;
  };
  tcrBillable: boolean;
  vettingClasses: VettingClassType[];
}

export interface CampaignOperation {
  campaignUid: string;
  networkId: number;
  status: string;
  checklistJson?: Record<string, unknown>;
  osrAttributesJson?: Record<string, unknown>;
  rulesEngineResultsJson?: Record<string, unknown>;
  createDate: number;
  updateDate: number;
}

export interface CnpCampaignsParams {
  channelId?: string;
  campaignUid?: string;
}

export interface CampaignDetail {
  uid: string;
  fallbackCampaignUid: string | null;
  referenceId: string | null;
  brandUid: string;
  cspUid: string;
  resellerUid: string;
  autoRenewal: boolean;
  tpm: number;
  description: string;
  usecase: string; // OTP, TRANSACTIONAL, PROMOTIONAL, MULTI_USE
  subUsecases?: string;
  status: string;
  campaignScale: string;
  embeddedLink: boolean;
  embeddedPhone: boolean;
  termsAndConditions: boolean;
  ageGated: boolean;
  directLending: boolean;
  numberPool: boolean;
  subscriberOptin: boolean;
  subscriberOptout: boolean;
  subscriberHelp: boolean;
  sample1: string;
  sample2: string | null;
  sample3: string | null;
  sample4: string | null;
  sample5: string | null;
  messageFlow: string;
  optinKeywords: string;
  optoutKeywords: string;
  helpKeywords: string;
  optinMessage: string;
  optoutMessage: string;
  helpMessage: string;
  privacyPolicyLink: string;
  termsAndConditionsLink?: string;
  embeddedLinkSample?: string;
  createDate: number | Date;
  updateDate: number;
  expirationDate: number | null;
  nextRenewalOrExpirationDate: number | Date;
  brandName: string;
  brandCompanyName: string | null;
  resellerName: string | null;
  brand: {
    uid: string;
    entityType: string;
    companyName: string;
    website: string;
    evpVettingScore: number;
    optionalAttributes: Record<string, any>;
    displayName: string;
    phone: string;
    email: string;
    rcsVettingStatus: string | null;
    logoStatus: string | null;
    bannerStatus: string | null;
  };
  upstreamCnpName: string | null;
  countryCode: string;
  channelId: string;
  displayName: string | null;
  hostingRegion: string | null; // NORTH_AMERICA, EUROPE, ASIA_PACIFIC
  billingCategory: string | null; // BASIC_MESSAGE, SINGLE_MESSAGE, CONVERSATIONAL
  shortDescription: string | null;
  color: string | null;
  phones: string[] | null;
  phoneLabels: string[] | null;
  emails: string[] | null;
  emailLabels: string[] | null;
  websites: string[] | null;
  websiteLabels: string[] | null;
  technicalContactName: string | null;
  technicalContactEmail: string | null;
  technicalContactJobTitle: string | null;
  campaignLogo: string | null;
  campaignBanner: string | null;
  screenshotUrl: string | null;
  videoUrl: string | null;
  actionTriggerMessages: string | null;
  messageTypes: string | null;
  optinFlow: string | null;
  optoutFlow: string | null;

  verificationStatus?: string; // PENDING, UNVERIFIED, VERIFIED
  downstreamCnpName?: string;
  dcaCompletion: boolean;

  routingMode?: 'DEDICATED' | 'COMMON';
  offNetOutboundTerminationSupported?: boolean;
  intlInboundTerminationSupported?: boolean;
  intlOutboundTerminationSupported?: boolean;
  onNetInboundTerminationSupported?: boolean;
  offNetInboundTerminationSupported?: boolean;
  targetMNOs?: Array<number>;
  senderIdType?: 'LONGCODE' | 'SHORTCODE';
  expectedMessageVolume?: number;
  operationStatuses: Record<number, string> | null;
}

export interface CampaignVerification {
  campaignUid: string;
  type: string;
  value: string;
  evpUid: string;
  vettingId: string;
  vettingToken: string;
  attachmentUuidList: string;
  verificationStatus: string;
  verificationDate: number | null;
  validUntil: number | null;
  expirationDate: number | null;
  reasons: Array<string> | null;
  fullReport: Record<string, unknown> | null;
  mnoIds: Array<number>;
  createDate: number | null;
  updateDate: number | null;
}
export interface Attachment {
  uuid: string;
  fileName: string;
  mimeType: string;
}

export interface CampaignSuggestion {
  campaignUid: string;
  brandUid: string;
  cspUid: string;
  usecase: CampaignDetail['usecase'];
  status: CampaignDetail['status'];
  channelId: ChannelId;
  registeredDate: number;
}

export enum AttachmentFolder {
  MmsSampleMedia = 'MMS_SAMPLE_MEDIA',
  SupportingDocument = 'SUPPORTING_DOCUMENT',
  VetAppeal = 'VET_APPEAL',
  BrandAssetLogo = 'BRAND_ASSET_LOGO',
  BrandAssetBanner = 'BRAND_ASSET_BANNER',
  ExternalVettingReport = 'EXTERNAL_VETTING_REPORT',
}

export interface VerticalType {
  id: string;
  displayName: string;
  description: string;
}

export interface Dca {
  uid: string;
  displayName: string;
  escalateEmail: string;
  escalateEmailVerifiedDate: number | null;
  companyLogo: string | null;
  osrProvisionerId: string | null;
  createDate: number;
  updateDate: number;
}

export interface ValidationRule {
  entityType: string;
  attributeName: string;
  country: string;
  regulation: string;
  structure: string;
  example: string;
  regex: string;
  referenceLink: string;
}

export interface CspProfilePayload {
  entityType: string;
  displayName: string;
  companyName: string;
  stockSymbol?: string;
  stockExchange?: string;
  street: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  email: string;
  escalateEmail: string;
  techEmail: string;
  financeEmail: string;
  website: string;
  phone: string;
  ein: string;
  questionnairePrimaryCnpName: string;
  questionnaireSecondaryCnpName?: string;
  questionnaireRelationshipLength?: string;
  questionnaireRelationshipLengthSecondaryCNP?: string;
  market: string;
  termsAndConditionsAcceptDate: string /** ISO format: YYYY-MM-DDTHH:mm:ssZ */;
  questionnaireSnowshoeAnswer?: string;
  questionnaireATOAnswer?: string;
}

export interface VettingAppeal {
  evpUid: string;
  cspUid: string;
  brandUid: string;
  vettingId: string;
  vettingClass: string;
  status: string;
  categoryList: string;
  attachmentUuidList: string | null;
  explanation: string | null;
  appealOutcome: string | null;
  fullReport: string | null;
  statusUpdateDate: number;
  createDate: number;
  updateDate: number;
  attachments: AttachmentInfo[] | null;
}

export interface CreateTrademarkParams {
  trademarkText: string;
  registrationId: string;
  patentOffice: string;
  trademarkOwner: string;
}

export interface Trademark {
  id: string;
  uid: string;
  cspUid: string;
  brandUid: string;
  registrationId: string;
  trademarkText: string;
  owner: string;
  patentOffice: string;
  validUntil: number;
  outcome: string;
  description: string;
  createDate: number;
  updateDate: number;
  associatedSenderIdUidList?: Array<{
    senderUid: string;
    senderId: string;
  }>;
}

export interface CreateSenderIdParams {
  senderId: string;
  associatedDcaList: string;
  protectedSenderIdList: string;
  trademarkUid: string;
}

export interface SenderId {
  id: string;
  uid: string;
  cspUid: string;
  brandUid: string;
  senderId: string;
  associatedDcaList: string;
  protectedSenderIdList: string | null;
  createDate: number;
  updateDate: number;
  trademarkUid: string | null;
}

export interface Loa {
  docUid: string;
  type: string;
  entityUid: string;
  entityType: string;
  status: string;
  createDate: number;
  updateDate: number;
  signDate: number;
}

export interface BillingReport {
  approved: boolean;
  month: string;
  createDate: number;
  updateDate: number;
}

export interface AuthenticatedUserSuggestionParams {
  prefix?: string;
}

export interface AuthenticatedUserSuggestion {
  type: string;
  scope: string;
  name: string;
  identifier: string;
}
export interface EventType {
  id: string;
  displayName: string;
  eventCategory: string;
}

export interface EventHistory {
  id: number;
  eventType: string;
  categoryType: string;
  campaignUid?: string;
  cspUid: string;
  dcaUid?: string;
  mnoId?: number;
  evpUid?: string;
  brandUid: string;
  description: string;
  campaignUsecase?: string;
  campaignVertical?: string;
  createDate: number | Date;
  updateDate?: number | Date;
  brandName?: string;
}

export type CspCampaign = Omit<
  CampaignDetail,
  | 'fallbackCampaignUid'
  | 'hostingRegion'
  | 'billingCategory'
  | 'shortDescription'
  | 'color'
  | 'phones'
  | 'phoneLabels'
  | 'emails'
  | 'emailLabels'
  | 'websites'
  | 'websiteLabels'
  | 'technicalContactName'
  | 'technicalContactEmail'
  | 'technicalContactJobTitle'
  | 'campaignLogo'
  | 'campaignBanner'
  | 'screenshotUrl'
  | 'videoUrl'
  | 'actionTriggerMessages'
  | 'messageTypes'
  | 'optinFlow'
  | 'optoutFlow'
>;

export interface SenderIdInventory {
  id: number;
  uid: string;
  networkId: number;
  senderIdValue: string;
  senderIdType: 'LONGCODE' | 'SHORTCODE';
  market: string;
  sharingModel: string;
  routingMode: string;
  status: string;
  createDate: number;
  updateDate: number;
}

export interface MnoEnum {
  networkId: number;
  displayName: string;
  osrBitmaskIndex: number;
}

export interface SuspensionCategory {
  id: string;
  name: string;
  description: string;
}

export type CampaignActionEligibility = {
  startMigration: boolean;
  cancelMigration: boolean;
  shareCampaign: boolean;
  declineCampaign: boolean;
  suspendCampaign: boolean;
  unsuspendCampaign: boolean;
};

export type Resend2faParams = {
  vettingClass: string;
};

export enum CampaignStatus {
  Active = 'ACTIVE',
  Expired = 'EXPIRED',
}

export enum CampaignSharingStatus {
  Pending = 'PENDING',
  Accepted = 'ACCEPTED',
  Declined = 'DECLINED',
}
